// The one header, for every module (spec, The header). Written in JSX + Tailwind with
// semantic tokens only, so it matches Support and every kit component: all read tokens.css.
import { Link, NavLink } from 'react-router';

import { useSession } from './session/SessionProvider.jsx';
import { productRole } from './session/roles.js';
import { visibleNav } from './module-logic.js';
import ModuleSwitcher from './ModuleSwitcher.jsx';
import NotificationBell from './notifications/NotificationBell.jsx';
import ThemeToggle from './ThemeToggle.jsx';

function initials(name) {
  return String(name ?? '').split(/\s+/).filter(Boolean).slice(0, 2)
    .map((word) => word[0].toUpperCase()).join('');
}

// The module's two header slots are NOT rendered here (D-046). The header sits outside the
// module's Provider, so a Provider that withholds its children cannot take the switcher or
// sign-out with it; the slots need that Provider's context, so PlatformLayout renders them
// inside it and portals them into the two targets below (`navExtrasTarget`,
// `actionsTarget`: stable ref callbacks). Each target is `display: contents`, so whatever a
// slot renders lays out as direct items of the flex row, exactly as before, and an empty
// target takes no room and adds no gap.
export default function PlatformHeader({ active, available, entry, onSignOut, signingOut, navExtrasTarget, actionsTarget }) {
  const user = useSession();
  const nav = active && entry ? visibleNav(entry.nav, user, active.product) : [];
  // The role in the ACTIVE module's product. `user.role` is the Support projection only
  // and would be wrong in every other module.
  const role = active ? productRole(user, active.product) : null;

  return (
    <nav className="bg-surface border-b border-line">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              {/* The ACTIVE module's home, not "/": "/" would leave the module and come
                  back, remounting it and losing its in-page state (Support's kept
                  feature-request text, for one). With no active module, "/" lands. */}
              <Link to={active?.home ?? '/'} aria-label={active ? `${active.label} home` : 'Adora Ops home'}
                className="shrink-0">
                <img src="/adora-256.png" alt="" className="size-7 object-contain" width="28" height="28" />
              </Link>
              <ModuleSwitcher active={active} available={available} />
            </div>
            <div className="hidden sm:flex items-center gap-1">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end === true}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      isActive ? 'bg-sunken text-ink' : 'text-ink-secondary hover:text-ink hover:bg-sunken'
                    }`}
                >
                  {item.label}
                </NavLink>
              ))}
              {/* The module's HeaderNavExtras lands here (PlatformLayout's ModuleSlots). */}
              <span ref={navExtrasTarget} className="contents" />
            </div>
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            {/* The module's HeaderActions lands here (PlatformLayout's ModuleSlots). */}
            <span ref={actionsTarget} className="contents" />
            {/* The bell (ROADMAP #4): a shell control, in every module, outside the module's
                Provider (D-046). It renders nothing until the server answers, and nothing at
                all if the endpoint is missing. */}
            <NotificationBell />
            <ThemeToggle />
            <div className="flex items-center gap-2 pl-3 border-l border-line">
              <div className="hidden sm:block text-right leading-tight">
                <div className="text-sm font-medium text-ink">{user.display_name}</div>
                {role && <div className="text-xs text-ink-muted capitalize">{role}</div>}
              </div>
              <div className="grid size-8 place-items-center rounded-full bg-sunken text-xs font-medium text-ink-secondary ring-1 ring-inset ring-line">
                {initials(user.display_name)}
              </div>
              {/* Hidden under the stub identity: with SSO off there is no /auth router,
                  so there is nothing to sign out of (unchanged from Support's header). */}
              {!user.auth_is_stub && (
                <button type="button" className="btn-ghost" onClick={onSignOut} disabled={signingOut}>
                  {signingOut ? 'Signing out…' : 'Sign out'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
